import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import './RedTeamPlanning.css';

const RISK_COLOR = { CRITICAL: '#dc2626', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: '#22c55e' };
const DETECT_COLOR = { HIGH: '#dc2626', MEDIUM: '#f59e0b', LOW: '#22c55e' };
const STATUS_COLOR = { COMPLETED: '#22c55e', GENERATING: '#3b82f6', FAILED: '#dc2626', PENDING: '#6b7280' };

function token(t) { return localStorage.getItem('authToken'); }

// ── Estimation Panel ────────────────────────────────────────────────────────
function EstimationPanel({ domainId, onGenerate }) {
  const { t } = useTranslation();
  const [est, setEst]       = useState(null);
  const [loading, setLoad]  = useState(false);
  const [err, setErr]       = useState(null);

  const runEstimate = useCallback(async () => {
    if (!domainId) return;
    setLoad(true); setErr(null); setEst(null);
    try {
      const r = await axiosInstance.get('/red-team/estimate', {
        params: { domainId },
        headers: { Authorization: `Bearer ${token()}` },
      });
      setEst(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setLoad(false); }
  }, [domainId]);

  useEffect(() => { if (domainId) runEstimate(); }, [domainId, runEstimate]);

  if (!domainId) return null;
  if (loading)   return <div className="rt-est-card rt-shimmer"><div className="rt-shimmer-line" /><div className="rt-shimmer-line rt-shimmer-short" /></div>;
  if (err)       return <div className="rt-est-card rt-est-error"><i className="fas fa-exclamation-circle" /> {err}</div>;
  if (!est)      return null;

  return (
    <div className="rt-est-card">
      <div className="rt-est-header">
        <i className="fas fa-calculator rt-est-icon" />
        <span>{t('RedTeamPlanning.estTitlePrefix')} <strong>{est.domainName}</strong></span>
      </div>
      <div className="rt-est-grid">
        <div className="rt-est-metric">
          <div className="rt-est-value">{est.subdomainCount?.toLocaleString()}</div>
          <div className="rt-est-label">{t('RedTeamPlanning.subdomains')}</div>
        </div>
        <div className="rt-est-metric">
          <div className="rt-est-value">{est.cveCount?.toLocaleString()}</div>
          <div className="rt-est-label">{t('RedTeamPlanning.findings')}</div>
        </div>
        <div className="rt-est-metric">
          <div className="rt-est-value">{est.estimatedInputTokens?.toLocaleString()}</div>
          <div className="rt-est-label">{t('RedTeamPlanning.inputTokens')}</div>
        </div>
        <div className="rt-est-metric">
          <div className="rt-est-value">{est.estimatedOutputTokens?.toLocaleString()}</div>
          <div className="rt-est-label">{t('RedTeamPlanning.outputTokens')}</div>
        </div>
        <div className="rt-est-metric rt-est-metric--highlight">
          <div className="rt-est-value">{est.estimatedTotalTokens?.toLocaleString()}</div>
          <div className="rt-est-label">{t('RedTeamPlanning.totalTokens')}</div>
        </div>
        <div className="rt-est-metric rt-est-metric--cost">
          <div className="rt-est-value">${est.estimatedCostUsd}</div>
          <div className="rt-est-label">{t('RedTeamPlanning.estCostUsd')}</div>
        </div>
      </div>
      <div className="rt-est-model">{t('RedTeamPlanning.modelLabel')} <code>{est.model}</code></div>
      <div className="rt-est-note"><i className="fas fa-info-circle" /> {est.note}</div>
      <button className="rt-btn rt-btn-generate" onClick={() => onGenerate(domainId)}>
        <i className="fas fa-robot" /> {t('RedTeamPlanning.generateAttackPlan')}
      </button>
    </div>
  );
}

// ── Attack Phase Card ────────────────────────────────────────────────────────
function PhaseCard({ phase, index }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(index === 0);

  return (
    <div className="rt-phase-card">
      <div className="rt-phase-header" onClick={() => setOpen(o => !o)}>
        <span className="rt-phase-num">{t('RedTeamPlanning.phaseNum', { number: phase.phaseNumber })}</span>
        <span className="rt-phase-name">{phase.phaseName}</span>
        <span className="rt-detect-badge" style={{ background: DETECT_COLOR[phase.detectionRisk] || '#6b7280' }}>
          {t('RedTeamPlanning.detectionRisk', { risk: phase.detectionRisk })}
        </span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} rt-phase-chevron`} />
      </div>

      {open && (
        <div className="rt-phase-body">
          <div className="rt-phase-row">
            <div className="rt-phase-col">
              <h4>{t('RedTeamPlanning.objective')}</h4>
              <p>{phase.objective}</p>
            </div>
            <div className="rt-phase-col">
              <h4>{t('RedTeamPlanning.expectedOutcome')}</h4>
              <p>{phase.expectedOutcome}</p>
            </div>
          </div>

          {phase.prerequisites && (
            <div className="rt-phase-section">
              <h4><i className="fas fa-lock" /> {t('RedTeamPlanning.prerequisites')}</h4>
              <p>{phase.prerequisites}</p>
            </div>
          )}

          {phase.steps?.length > 0 && (
            <div className="rt-phase-section">
              <h4><i className="fas fa-list-ol" /> {t('RedTeamPlanning.attackSteps')}</h4>
              <ol className="rt-steps">
                {phase.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}

          <div className="rt-phase-tags-row">
            {phase.techniquesUsed?.length > 0 && (
              <div className="rt-tag-group">
                <span className="rt-tag-label">{t('RedTeamPlanning.mitreAttack')}</span>
                {phase.techniquesUsed.map(t => (
                  <a key={t} href={`https://attack.mitre.org/techniques/${t.replace('.','/').replace('T','T')}`}
                     target="_blank" rel="noreferrer" className="rt-tag rt-tag--mitre">{t}</a>
                ))}
              </div>
            )}
            {phase.tools?.length > 0 && (
              <div className="rt-tag-group">
                <span className="rt-tag-label">{t('RedTeamPlanning.tools')}</span>
                {phase.tools.map(t => <span key={t} className="rt-tag rt-tag--tool">{t}</span>)}
              </div>
            )}
            {phase.exploitedCves?.length > 0 && (
              <div className="rt-tag-group">
                <span className="rt-tag-label">{t('RedTeamPlanning.cves')}</span>
                {phase.exploitedCves.map(c => <span key={c} className="rt-tag rt-tag--cve">{c}</span>)}
              </div>
            )}
            {phase.targetAssets?.length > 0 && (
              <div className="rt-tag-group">
                <span className="rt-tag-label">{t('RedTeamPlanning.targets')}</span>
                {phase.targetAssets.map(a => <span key={a} className="rt-tag rt-tag--asset">{a}</span>)}
              </div>
            )}
          </div>

          {phase.mitigations?.length > 0 && (
            <div className="rt-phase-section rt-mitigations">
              <h4><i className="fas fa-shield-alt" /> {t('RedTeamPlanning.breakKillChain')}</h4>
              <ul>{phase.mitigations.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Plan Detail Modal ────────────────────────────────────────────────────────
function PlanDetailModal({ planId, onClose }) {
  const { t } = useTranslation();
  const [plan, setPlan]   = useState(null);
  const [load, setLoad]   = useState(true);
  const [err, setErr]     = useState(null);
  const [schedCron, setSchedCron] = useState('daily');
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedSaving, setSchedSaving]   = useState(false);
  const [schedMsg, setSchedMsg]         = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get(`/red-team/plans/${planId}`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setPlan(r.data);
        setSchedCron(r.data.scheduleCron || 'daily');
        setSchedEnabled(!!r.data.scheduleEnabled);
      } catch (e) { setErr(e.message); }
      finally { setLoad(false); }
    })();
  }, [planId]);

  const saveSchedule = async () => {
    setSchedSaving(true); setSchedMsg(null);
    try {
      await axiosInstance.post(`/red-team/plans/${planId}/schedule`,
        { cron: schedCron, enabled: schedEnabled },
        { headers: { Authorization: `Bearer ${token()}` } });
      setSchedMsg(schedEnabled ? t('RedTeamPlanning.scheduled', { cron: schedCron }) : t('RedTeamPlanning.scheduleDisabled'));
    } catch (e) { setSchedMsg(t('RedTeamPlanning.scheduleFailed', { message: e.message })); }
    finally { setSchedSaving(false); }
  };

  return (
    <div className="rt-modal-overlay" onClick={onClose}>
      <div className="rt-modal" onClick={e => e.stopPropagation()}>
        <div className="rt-modal-header">
          <span>{t('RedTeamPlanning.attackPlanNum', { id: planId })}</span>
          <button className="rt-modal-close" onClick={onClose}><i className="fas fa-times" /></button>
        </div>

        {load && <div className="rt-modal-loading"><i className="fas fa-spinner fa-spin" /> {t('RedTeamPlanning.loadingPlan')}</div>}
        {err  && <div className="rt-modal-err">{err}</div>}

        {plan && !load && (
          <div className="rt-modal-body">
            {/* Executive Summary */}
            <div className="rt-modal-section">
              <div className="rt-plan-meta-row">
                <span className="rt-risk-badge" style={{ background: RISK_COLOR[plan.riskRating] || '#6b7280' }}>
                  {plan.riskRating}
                </span>
                <span className="rt-meta-item"><i className="fas fa-clock" /> {plan.estimatedTta}</span>
                <span className="rt-meta-item"><i className="fas fa-coins" /> ${plan.costActualUsd || plan.costEstimateUsd} USD</span>
                <span className="rt-meta-item"><i className="fas fa-microchip" /> {plan.tokenActual || plan.tokenEstimate} tokens</span>
                <span className="rt-meta-item"><i className="fas fa-calendar" /> {new Date(plan.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="rt-threat-profile">
                <strong>{t('RedTeamPlanning.threatActorProfile')}</strong> {plan.threatActorProfile}
              </div>
              <div className="rt-exec-summary">{plan.executiveSummary}</div>
            </div>

            {/* Narrative */}
            {plan.attackNarrative && (
              <div className="rt-modal-section">
                <h3><i className="fas fa-scroll" /> {t('RedTeamPlanning.attackNarrative')}</h3>
                <p className="rt-narrative">{plan.attackNarrative}</p>
              </div>
            )}

            {/* Critical Findings */}
            {plan.criticalFindings?.length > 0 && (
              <div className="rt-modal-section">
                <h3><i className="fas fa-crosshairs" /> {t('RedTeamPlanning.criticalFindings')}</h3>
                <ul className="rt-critical-list">
                  {plan.criticalFindings.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}

            {/* Phases */}
            <div className="rt-modal-section">
              <h3><i className="fas fa-sitemap" /> {t('RedTeamPlanning.attackPhases', { count: plan.phases?.length })}</h3>
              {plan.phases?.map((ph, i) => <PhaseCard key={ph.id} phase={ph} index={i} />)}
            </div>

            {/* Remediations */}
            {plan.recommendedRemediations?.length > 0 && (
              <div className="rt-modal-section rt-remed-section">
                <h3><i className="fas fa-tools" /> {t('RedTeamPlanning.recommendedRemediations')}</h3>
                <ol className="rt-remed-list">
                  {plan.recommendedRemediations.map((r, i) => <li key={i}>{r}</li>)}
                </ol>
              </div>
            )}

            {/* Schedule */}
            <div className="rt-modal-section rt-schedule-section">
              <h3><i className="fas fa-calendar-alt" /> {t('RedTeamPlanning.scheduleAutomation')}</h3>
              <div className="rt-schedule-row">
                <select value={schedCron} onChange={e => setSchedCron(e.target.value)} className="rt-select">
                  <option value="daily">{t('RedTeamPlanning.daily')}</option>
                  <option value="weekly">{t('RedTeamPlanning.weekly')}</option>
                  <option value="monthly">{t('RedTeamPlanning.monthly')}</option>
                </select>
                <label className="rt-toggle">
                  <input type="checkbox" checked={schedEnabled} onChange={e => setSchedEnabled(e.target.checked)} />
                  <span className="rt-toggle-label">{schedEnabled ? t('RedTeamPlanning.enabled') : t('RedTeamPlanning.disabled')}</span>
                </label>
                <button className="rt-btn rt-btn-sm" onClick={saveSchedule} disabled={schedSaving}>
                  {schedSaving ? <i className="fas fa-spinner fa-spin" /> : t('RedTeamPlanning.saveSchedule')}
                </button>
                {schedMsg && <span className="rt-sched-msg">{schedMsg}</span>}
              </div>
              {plan.nextScheduledRun && (
                <div className="rt-next-run">
                  {t('RedTeamPlanning.nextRun', { datetime: new Date(plan.nextScheduledRun).toLocaleString() })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Credit Allocation Panel ──────────────────────────────────────────────────
function CreditPanel() {
  const { t } = useTranslation();
  const [partnerId, setPartnerId] = useState('');
  const [credits, setCredits]     = useState('5');
  const [msg, setMsg]             = useState(null);
  const [saving, setSaving]       = useState(false);

  const allocate = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await axiosInstance.post('/red-team/admin/credits',
        { partnerId: Number(partnerId), credits: Number(credits) },
        { headers: { Authorization: `Bearer ${token()}` } });
      setMsg(t('RedTeamPlanning.creditsAllocated', { firmName: r.data.firmName, credits: r.data.redTeamCredits }));
    } catch (e) { setMsg(t('RedTeamPlanning.creditsError', { message: e.response?.data?.error || e.message })); }
    finally { setSaving(false); }
  };

  return (
    <div className="rt-credit-panel">
      <h3><i className="fas fa-coins" /> {t('RedTeamPlanning.partnerCreditAllocation')}</h3>
      <div className="rt-credit-row">
        <input className="rt-input" placeholder={t('RedTeamPlanning.partnerIdPlaceholder')} value={partnerId}
          onChange={e => setPartnerId(e.target.value)} type="number" min="1" />
        <input className="rt-input rt-input-sm" placeholder={t('RedTeamPlanning.creditsPlaceholder')} value={credits}
          onChange={e => setCredits(e.target.value)} type="number" min="1" max="100" />
        <button className="rt-btn rt-btn-sm" onClick={allocate} disabled={saving || !partnerId}>
          {saving ? <i className="fas fa-spinner fa-spin" /> : t('RedTeamPlanning.allocate')}
        </button>
      </div>
      {msg && <div className="rt-credit-msg">{msg}</div>}
      <p className="rt-credit-note">{t('RedTeamPlanning.creditNote')}</p>
    </div>
  );
}

// ── Plans History Table ──────────────────────────────────────────────────────
function PlansTable({ domainId, refresh }) {
  const { t } = useTranslation();
  const [plans, setPlans]     = useState([]);
  const [load, setLoad]       = useState(false);
  const [selectedId, setSelId] = useState(null);

  const loadPlans = useCallback(async () => {
    if (!domainId) return;
    setLoad(true);
    try {
      const r = await axiosInstance.get('/red-team/plans',
        { params: { domainId }, headers: { Authorization: `Bearer ${token()}` } });
      setPlans(r.data);
    } catch (e) { /* silent */ }
    finally { setLoad(false); }
  }, [domainId]);

  useEffect(() => { loadPlans(); }, [loadPlans, refresh]);

  if (!domainId) return null;

  return (
    <div className="rt-plans-section">
      <div className="rt-plans-header">
        <h3><i className="fas fa-history" /> {t('RedTeamPlanning.attackPlanHistory')}</h3>
        <button className="rt-btn rt-btn-sm rt-btn-ghost" onClick={loadPlans}>
          <i className={`fas fa-sync ${load ? 'fa-spin' : ''}`} />
        </button>
      </div>
      {load && <div className="rt-loading-row"><i className="fas fa-spinner fa-spin" /> {t('RedTeamPlanning.loading')}</div>}
      {!load && plans.length === 0 && <div className="rt-empty">{t('RedTeamPlanning.noPlans')}</div>}
      {plans.length > 0 && (
        <table className="rt-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('RedTeamPlanning.colStatus')}</th>
              <th>{t('RedTeamPlanning.colRisk')}</th>
              <th>{t('RedTeamPlanning.colPhases')}</th>
              <th>{t('RedTeamPlanning.colTokens')}</th>
              <th>{t('RedTeamPlanning.colCost')}</th>
              <th>{t('RedTeamPlanning.colCreated')}</th>
              <th>{t('RedTeamPlanning.colSchedule')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {plans.map(p => (
              <tr key={p.id} className="rt-table-row">
                <td className="rt-id-cell">#{p.id}</td>
                <td>
                  <span className="rt-status-dot" style={{ background: STATUS_COLOR[p.status] || '#6b7280' }} />
                  {p.status}
                </td>
                <td>
                  {p.riskRating && (
                    <span className="rt-risk-pill" style={{ background: RISK_COLOR[p.riskRating] || '#6b7280' }}>
                      {p.riskRating}
                    </span>
                  )}
                </td>
                <td>{p.phaseCount ?? '–'}</td>
                <td>{p.tokenActual ? p.tokenActual.toLocaleString() : (p.tokenEstimate ? `~${p.tokenEstimate.toLocaleString()}` : '–')}</td>
                <td>${p.costActualUsd ?? p.costEstimateUsd ?? '–'}</td>
                <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '–'}</td>
                <td>
                  {p.scheduleEnabled
                    ? <span className="rt-sched-active"><i className="fas fa-calendar-check" /> {p.scheduleCron}</span>
                    : <span className="rt-sched-off">–</span>}
                </td>
                <td>
                  <button className="rt-btn rt-btn-sm rt-btn-ghost" onClick={() => setSelId(p.id)}>
                    <i className="fas fa-eye" /> {t('RedTeamPlanning.view')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {selectedId && <PlanDetailModal planId={selectedId} onClose={() => setSelId(null)} />}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RedTeamPlanning() {
  const { t } = useTranslation();
  const [domains, setDomains]     = useState([]);
  const [domainId, setDomainId]   = useState('');
  const [generating, setGen]      = useState(false);
  const [genErr, setGenErr]       = useState(null);
  const [genResult, setGenResult] = useState(null);
  const [refresh, setRefresh]     = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get('/asm/domains/names', {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setDomains(r.data || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const handleGenerate = async (dId) => {
    setGen(true); setGenErr(null); setGenResult(null);
    try {
      const r = await axiosInstance.post('/red-team/generate',
        { domainId: Number(dId) },
        { headers: { Authorization: `Bearer ${token()}` } });
      setGenResult(r.data);
      setRefresh(n => n + 1);
    } catch (e) {
      const status = e.response?.status;
      const msg    = e.response?.data?.error || e.message;
      if (status === 402) setGenErr(t('RedTeamPlanning.paymentRequired', { message: msg }));
      else                setGenErr(t('RedTeamPlanning.generationFailed', { message: msg }));
    } finally { setGen(false); }
  };

  return (
    <div className="rt-page">
      <div className="rt-page-header">
        <div className="rt-page-title">
          <i className="fas fa-skull-crossbones rt-title-icon" />
          <div>
            <h2>{t('RedTeamPlanning.pageTitle')}</h2>
            <p>{t('RedTeamPlanning.pageSubtitle')}</p>
          </div>
        </div>
        <div className="rt-page-badge">
          <i className="fas fa-robot" /> {t('RedTeamPlanning.poweredBy')}
        </div>
      </div>

      {/* Domain selector */}
      <div className="rt-domain-section">
        <label className="rt-label">{t('RedTeamPlanning.selectTargetDomain')}</label>
        <select className="rt-select rt-domain-select"
          value={domainId} onChange={e => { setDomainId(e.target.value); setGenResult(null); setGenErr(null); }}>
          <option value="">{t('RedTeamPlanning.chooseDomain')}</option>
          {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {/* Token estimate + generate button */}
      {domainId && !generating && !genResult && (
        <EstimationPanel domainId={Number(domainId)} onGenerate={handleGenerate} />
      )}

      {/* Generating spinner */}
      {generating && (
        <div className="rt-generating-card">
          <div className="rt-gen-spinner"><i className="fas fa-brain fa-spin rt-gen-icon" /></div>
          <div className="rt-gen-text">
            <strong>{t('RedTeamPlanning.planningAttack')}</strong>
            <span>{t('RedTeamPlanning.analyzingFindings', { domain: domains.find(d => String(d.id) === String(domainId))?.name })}</span>
          </div>
        </div>
      )}

      {/* Generation error */}
      {genErr && (
        <div className="rt-gen-error">
          <i className="fas fa-exclamation-triangle" /> {genErr}
          <button className="rt-btn rt-btn-sm rt-btn-ghost" onClick={() => setGenErr(null)}>{t('RedTeamPlanning.dismiss')}</button>
        </div>
      )}

      {/* Generation success banner */}
      {genResult && (
        <div className="rt-gen-success">
          <i className="fas fa-check-circle" />
          <div>
            <strong>{t('RedTeamPlanning.planGeneratedRisk')}</strong>
            <span className="rt-risk-inline" style={{ color: RISK_COLOR[genResult.riskRating] }}>
              {genResult.riskRating}
            </span>
            <span>{t('RedTeamPlanning.planGeneratedSummary', { phases: genResult.phaseCount, tta: genResult.estimatedTta })}</span>
          </div>
          <button className="rt-btn rt-btn-sm" onClick={() => setRefresh(n => n + 1)}>
            <i className="fas fa-eye" /> {t('RedTeamPlanning.viewPlan')}
          </button>
        </div>
      )}

      {/* Credit allocation */}
      <CreditPanel />

      {/* History table */}
      {domainId && <PlansTable domainId={Number(domainId)} refresh={refresh} />}
    </div>
  );
}
